import React from 'react';
import { IDeleteItemDialog } from './interfaces';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteItem } from '../../../../reactQuery/opportunities';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

export const DeleteItemDialog: React.FC<IDeleteItemDialog> = ({
  show,
  item,
  onClose,
  onDeleted,
}): JSX.Element => {
  const { t } = useTranslation();
  const { mutate: deleteItem, isLoading: isDeletingItem } = useDeleteItem();
  const queryClient = useQueryClient();

  const handleDelete = () => {
    deleteItem(item.id as number, {
      onSuccess: () => {
        enqueueSnackbar(t('opportunities:prompts.deleteItem.success'), {
          variant: 'success',
        });

        queryClient.invalidateQueries({
          queryKey: ['opportunity-stages', { includeItems: true }],
        });

        if (onDeleted) {
          onDeleted();
        }

        onClose();
      },
      onError: () => {
        enqueueSnackbar(t('opportunities:prompts.deleteItem.failed'), {
          variant: 'error',
        });
      },
    });
  };

  return (
    <Dialog open={show}>
      <DialogTitle fontWeight="bold">
        {t('opportunities:prompts.deleteItem.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Trans
            i18nKey="opportunities:prompts.deleteItem.body"
            components={{ strong: <strong /> }}
            values={{ title: item.title }}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={isDeletingItem}>
          {t('buttons.cancel')}
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={isDeletingItem}
        >
          {t('buttons.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
